import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  ChessUser, 
  InitUserRequest, 
  LeaderboardResponse, 
  PostsResponse,
  ChessPost 
} from '@/types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';
    console.log('API Client initialized with baseURL:', baseURL);
    console.log('Environment variables:', {
      VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
      NODE_ENV: import.meta.env.NODE_ENV,
      MODE: import.meta.env.MODE,
    });
    
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth headers
    this.client.interceptors.request.use(
      (config) => {
        console.log('API Request:', {
          method: config.method,
          url: config.url,
          baseURL: config.baseURL,
          fullURL: `${config.baseURL}${config.url}`,
        });
        
        const userData = this.getUserData();
        console.log('User data for request:', userData);
        
        if (userData) {
          config.headers['X-User-Email'] = userData.email;
          config.headers['X-User-Name'] = userData.name;
          if (userData.gasUserId) {
            config.headers['X-Gas-User-Id'] = userData.gasUserId;
          }
        }
        
        console.log('Request headers:', config.headers);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log('API Response:', {
          status: response.status,
          url: response.config.url,
          data: response.data,
        });
        return response;
      },
      (error) => {
        console.error('API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.message,
          response: error.response?.data,
        });
        
        if (error.response?.status === 403) {
          // Handle domain restriction
          console.error('Access restricted to Mirats Group employees');
        }
        return Promise.reject(error);
      }
    );
  }

        private getUserData(): { email: string; name: string; gasUserId?: string } | null {
    // This would be replaced with actual Mirats SSO integration
    const userData = localStorage.getItem('userData');
    const parsedData = userData ? JSON.parse(userData) : null;
    console.log('API Client: getUserData from localStorage:', parsedData);
    return parsedData;
  }

  // Health check
  async getHealth(): Promise<ApiResponse> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // User management
  async initUser(data: InitUserRequest): Promise<ApiResponse<ChessUser>> {
    const response = await this.client.post('/user/init', data);
    return response.data;
  }

  async syncUser(): Promise<ApiResponse<ChessUser>> {
    const response = await this.client.post('/user/sync');
    return response.data;
  }

  async getUserProfile(): Promise<ApiResponse<ChessUser>> {
    const response = await this.client.get('/user/profile');
    return response.data;
  }

  async getLeaderboard(sort: string = 'rapid'): Promise<ApiResponse<LeaderboardResponse>> {
    const response = await this.client.get(`/user/leaderboard?sort=${sort}`);
    return response.data;
  }

  // Community posts
  async createPost(content: string): Promise<ApiResponse<ChessPost>> {
    const response = await this.client.post('/community/post', { content });
    return response.data;
  }

  async editPost(postId: string, content: string): Promise<ApiResponse<ChessPost>> {
    const response = await this.client.put(`/community/post/${postId}`, { content });
    return response.data;
  }

  async getPosts(page: number = 1, limit: number = 20): Promise<ApiResponse<PostsResponse>> {
    const response = await this.client.get(`/community/posts?page=${page}&limit=${limit}`);
    return response.data;
  }

  async getRecentPosts(): Promise<ApiResponse<ChessPost[]>> {
    const response = await this.client.get('/community/posts/recent');
    return response.data;
  }

  async getAllMembers(): Promise<ApiResponse<{ members: ChessUser[] }>> {
    const response = await this.client.get('/user/members');
    return response.data;
  }

  async validateChessUsername(username: string): Promise<boolean> {
    try {
      const response = await this.client.post('/user/validate-username', { username });
      return response.data.success;
    } catch (error) {
      return false;
    }
  }

  async createUserWithoutChess(data: { gasUserId: string; email: string; name: string }): Promise<ApiResponse<ChessUser>> {
    const response = await this.client.post('/user/create-without-chess', data);
    return response.data;
  }

  async connectChessProfile(data: { chessUsername: string }): Promise<ApiResponse<ChessUser>> {
    const response = await this.client.post('/user/connect-chess', data);
    return response.data;
  }
}

export const apiClient = new ApiClient(); 